import { ReactElement, useCallback, useEffect, useState } from 'react';
import { Box, Dialog, Typography, CircularProgress, Link } from '@mui/material';
import { Trans, type TransProps, useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { color } from '@/colors';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { ZIndex } from '@/consts/z-indexes.consts';
import { useConfirmationDialogStore } from '@/state/confirmation-dialog.state';
import { FlexCenVer } from '../styles';
import { AppXBtn } from '../AppXBtn/AppXBtn';
import { AppCheckbox } from '../AppCheckbox/AppCheckbox';
import { TextStdRg } from '../AppTypography/AppTypography';
import type { ConfirmationDialogData } from '@/state/confirmation-dialog.state';

const LINK_PROPS = {
  target: '_blank',
  rel: 'noopener noreferrer',
  sx: {
    '&:hover': {
      textDecoration: 'none',
    },
  },
};

const AppConfirmationDialogMessage = ({
  message,
  messageData,
}: {
  message?: string;
  messageData?: {
    i18n?: TransProps<string, string | readonly string[], string | undefined, string>;
    data: Record<string, string | ReactElement | undefined>;
  };
}) => {
  if (messageData) {
    const components: Record<string, ReactElement> = {};
    for (const [key, value] of Object.entries(messageData.data || {})) {
      if (typeof value === 'string') {
        components[key] = <Link href={value} {...LINK_PROPS} />;
      } else if (value) {
        components[key] = value;
      }
    }

    return (
      <TextStdRg color={color.White80_T} whiteSpace="pre-wrap" sx={{ mb: 4 }}>
        <Trans {...messageData.i18n} components={components} />
      </TextStdRg>
    );
  } else if (message) {
    return <TextStdRg color={color.White80_T} text={message} sx={{ mb: 4 }} whiteSpace="pre-wrap" />;
  }
};

interface AppConfirmationDialogStandaloneProps {
  dialogData: Omit<ConfirmationDialogData, 'onClose' | 'onConfirm'>;
  onConfirm: (checkboxChecked?: boolean) => void | Promise<void>;
  onClose: () => void;
}

/**
 * This is a standalone component. If you need to show it without using the global state for some reason, use this.
 */
export const AppConfirmationDialogStandalone = ({
  dialogData,
  onConfirm,
  onClose,
}: AppConfirmationDialogStandaloneProps) => {
  const {
    isOpen,
    title,
    message,
    messageData,
    confirmBtnText,
    cancelBtnText,
    disableBackdropClose,
    width = 460,
    checkboxLabel,
    isCheckboxChecked: initialCheckboxChecked,
  } = dialogData;
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [err, setErr] = useState('');
  const [checkboxChecked, setCheckboxChecked] = useState(initialCheckboxChecked ?? false);

  useEffect(() => {
    if (isOpen) {
      setCheckboxChecked(initialCheckboxChecked ?? false);
    }
  }, [isOpen, initialCheckboxChecked]);

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    setErr('');
    try {
      await onConfirm(checkboxLabel ? checkboxChecked : undefined);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Unknown error');
      throw new Error(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setIsLoading(false);
    }
  }, [onConfirm, checkboxLabel, checkboxChecked]);

  const handleClose = useCallback(
    (_event?: unknown, reason?: string) => {
      if (isLoading) return;
      if (disableBackdropClose && reason === 'backdropClick') return;
      onClose();
    },
    [disableBackdropClose, isLoading, onClose],
  );

  return (
    <Dialog
      onClose={handleClose}
      open={isOpen}
      sx={{ zIndex: ZIndex.Overlays.CONFIRMATION_DIALOGS }}
      slotProps={{
        paper: {
          sx: {
            background: color.Black92,
            border: `1px solid ${color.White08_T}`,
            px: 4,
            pt: 6,
            pb: 4,
            position: 'relative',
            width,
          },
        },
      }}
    >
      <AppXBtn onClick={handleClose} sx={{ position: 'absolute', top: 12, right: 12 }} disabled={isLoading} />
      <Typography variant="body-lg-sb" sx={{ mb: 3 }}>
        {title}
      </Typography>
      <AppConfirmationDialogMessage message={message} messageData={messageData} />
      {checkboxLabel && (
        <Box sx={{ mb: 2 }}>
          <AppCheckbox
            checked={checkboxChecked}
            onChange={(_, checked) => setCheckboxChecked(checked)}
            label={checkboxLabel}
          />
        </Box>
      )}
      <FlexCenVer sx={{ flexDirection: 'row-reverse', gap: 1 }}>
        <ButtonContained
          size="medium"
          onClick={() => void handleConfirm()}
          endIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
          disabled={isLoading}
          data-testid="app-confirmation-dialog-confirm-btn"
        >
          {confirmBtnText || t(I18N_KEYS.GENERAL.CONFIRM)}
        </ButtonContained>
        <ButtonContained size="medium" mode="text" onClick={handleClose} disabled={isLoading}>
          {cancelBtnText || t(I18N_KEYS.GENERAL.CANCEL)}
        </ButtonContained>
      </FlexCenVer>
      {err && <TextStdRg sx={{ mt: 1 }} text={err} whiteSpace="pre-wrap" color={color.Weavy_Error} />}
    </Dialog>
  );
};

const AppConfirmationDialog = () => {
  const { confirmationDialogData, closeConfirmationDialog } = useConfirmationDialogStore();

  const handleConfirm = useCallback(
    async (checkboxChecked?: boolean) => {
      await confirmationDialogData.onConfirm(checkboxChecked);
      closeConfirmationDialog();
    },
    [closeConfirmationDialog, confirmationDialogData],
  );

  const handleClose = useCallback(() => {
    void confirmationDialogData.onClose?.();
    closeConfirmationDialog();
  }, [closeConfirmationDialog, confirmationDialogData]);

  if (!confirmationDialogData.isOpen) {
    return null;
  }

  return (
    <AppConfirmationDialogStandalone
      dialogData={confirmationDialogData}
      onConfirm={handleConfirm}
      onClose={handleClose}
    />
  );
};

export default AppConfirmationDialog;
